// frontend/src/pages/Explore.js
// Public explore/browse page for discovering curated link collections

import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  Avatar,
  Stack,
  CircularProgress,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as PopularIcon,
  Schedule as RecentIcon,
  Tab as TabIcon,
  Star as StarIcon,
  CallSplit as CallSplitIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import axios from '../api/axiosConfig';
import { getSessionShareUrl } from '../utils/shareUrl';

const SessionCard = ({ session, onClick }) => {
  const theme = useTheme();
  const topDomains = session.topDomains || [];

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease, transform 0.15s ease',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardActionArea onClick={onClick} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        {/* Color accent */}
        <Box sx={{
          height: 4,
          background: `linear-gradient(90deg, ${session.color || theme.palette.primary.main}, ${theme.palette.secondary.main})`
        }} />
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Title */}
          <Typography variant="subtitle1" fontWeight={600} gutterBottom noWrap>
            {session.name}
          </Typography>

          {/* Description */}
          {session.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4
              }}
            >
              {session.description}
            </Typography>
          )}

          {/* Tags */}
          {session.tags?.length > 0 && (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {session.tags.slice(0, 3).map(tag => (
                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
              ))}
              {session.tags.length > 3 && (
                <Chip label={`+${session.tags.length - 3}`} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
              )}
            </Stack>
          )}

          {/* Domain favicons */}
          {topDomains.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, alignItems: 'center' }}>
              {topDomains.slice(0, 4).map((d, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <img
                    src={d.favicon || `https://www.google.com/s2/favicons?domain=${d.hostname}&sz=16`}
                    alt=""
                    style={{ width: 14, height: 14, borderRadius: 2 }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </Box>
              ))}
              {topDomains.length > 4 && (
                <Typography variant="caption" color="text.secondary">
                  +{topDomains.length - 4}
                </Typography>
              )}
            </Stack>
          )}

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Footer: stats + owner */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack direction="row" spacing={0.25} alignItems="center">
                <TabIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">{session.tabCount}</Typography>
              </Stack>
              {session.starCount > 0 && (
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                  <Typography variant="caption" color="text.secondary">{session.starCount}</Typography>
                </Stack>
              )}
              {session.forkCount > 0 && (
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <CallSplitIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">{session.forkCount}</Typography>
                </Stack>
              )}
              {session.sectionCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {session.sectionCount} section{session.sectionCount !== 1 ? 's' : ''}
                </Typography>
              )}
            </Stack>
            {session.owner?.username && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Avatar
                  src={session.owner.profilePictureUrl}
                  sx={{ width: 18, height: 18, fontSize: '0.6rem' }}
                >
                  {(session.owner.displayName || session.owner.username)?.[0]?.toUpperCase()}
                </Avatar>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>
                  @{session.owner.username}
                </Typography>
              </Stack>
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const Explore = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('popular');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchSessions = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (!append) { setLoading(true); setError(null); }
      else setLoadingMore(true);

      const params = {
        sort,
        page: pageNum,
        limit: 12,
        ...(debouncedSearch && { q: debouncedSearch })
      };

      const res = await axios.get('/api/explore', { params });
      const data = res.data;

      setSessions(prev => append ? [...prev, ...(data.sessions || [])] : (data.sessions || []));
      setHasMore(data.pagination?.hasMore || false);
      setTotal(data.pagination?.total || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch explore sessions:', err);
      if (!append) setError('Failed to load collections. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sort, debouncedSearch]);

  useEffect(() => {
    fetchSessions(1);
  }, [fetchSessions]);

  const handleSortChange = (_, newSort) => {
    if (newSort) setSort(newSort);
  };

  const handleCardClick = (session) => {
    if (!session.shareToken && !session.shareSlug) return;
    navigate(getSessionShareUrl(session, session.owner?.username));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 3, md: 5 }, mb: 6 }}>
      <Helmet>
        <title>Explore — Discover Curated Link Collections | Blackmount</title>
        <meta name="description" content="Discover curated link collections shared by the Blackmount community. Browse sessions organized by topic, domain, and popularity." />
        <link rel="canonical" href="https://app.blackmount.ai/explore" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Explore — Discover Curated Link Collections | Blackmount" />
        <meta property="og:description" content="Discover curated link collections shared by the Blackmount community." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.blackmount.ai/explore" />
        <meta property="og:image" content="https://app.blackmount.ai/og-image.png" />
        <meta property="og:site_name" content="Blackmount" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Explore — Discover Curated Link Collections | Blackmount" />
        <meta name="twitter:description" content="Discover curated link collections shared by the Blackmount community." />
        <meta name="twitter:image" content="https://app.blackmount.ai/og-image.png" />
      </Helmet>

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          component="img"
          src="/blackmount-logo.jpg"
          alt="Blackmount"
          sx={{ width: 40, height: 40, borderRadius: '50%', mb: 1.5, opacity: 0.85 }}
        />
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          Explore
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
          Discover curated link collections shared by the Blackmount community
        </Typography>
      </Box>

      {/* Controls: Search + Sort */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 4,
        alignItems: { sm: 'center' }
      }}>
        <TextField
          placeholder="Search collections..."
          inputProps={{ 'aria-label': 'Search collections' }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
        />
        <ToggleButtonGroup
          value={sort}
          aria-label="Sort collections"
          exclusive
          onChange={handleSortChange}
          size="small"
          sx={{ flexShrink: 0 }}
        >
          <ToggleButton value="popular" sx={{ textTransform: 'none', px: 2 }}>
            <PopularIcon sx={{ fontSize: 18, mr: 0.5 }} /> Popular
          </ToggleButton>
          <ToggleButton value="recent" sx={{ textTransform: 'none', px: 2 }}>
            <RecentIcon sx={{ fontSize: 18, mr: 0.5 }} /> Recent
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Results */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="outlined" onClick={() => fetchSessions(1)} sx={{ textTransform: 'none' }}>
            Retry
          </Button>
        </Box>
      ) : sessions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {debouncedSearch ? 'No collections found' : 'No collections yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {debouncedSearch
              ? `No results for "${debouncedSearch}". Try a different search.`
              : 'Be the first to create and share a curated collection!'}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={2.5}>
            {sessions.map((session, i) => (
              <Grid item xs={12} sm={6} md={4} key={session.shareToken || i}>
                <SessionCard
                  session={session}
                  onClick={() => handleCardClick(session)}
                />
              </Grid>
            ))}
          </Grid>

          {/* Total count */}
          {total > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
              Showing {sessions.length} of {total} collections
            </Typography>
          )}

          {/* Load More */}
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => fetchSessions(page + 1, true)}
                disabled={loadingMore}
                sx={{ textTransform: 'none', borderRadius: 3, px: 4 }}
              >
                {loadingMore ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Load More
              </Button>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default Explore;
